import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** Where Stripe Checkout brings a role subscription back to (ENG-892). */
export const UPGRADE_PATH = '/upgrade';

/**
 * The caller's role subscription (backend ENG-755, ENG-891), for the Upgrade
 * page. Returns `{ status, overview, error, reload, preview, change, cancel,
 * checkout }`:
 *
 * - `status` is 'loading' | 'ready' | 'error'; `overview` is the last
 *   `GET /me/subscription` read (kept while a reload runs).
 * - `reload()` reads it again and resolves to the new overview.
 * - `preview(role, interval, { replaceScheduled })` is
 *   `POST /me/subscription/preview`: what the change would cost.
 * - `change(role, interval, { prorationDate, replaceScheduled })` is
 *   `POST /me/subscription/change`, then a reload.
 * - `cancel()` is `POST /me/subscription/cancel`, then a reload.
 * - `checkout(role, interval)` opens Stripe Checkout and leaves the page;
 *   it comes back to `/upgrade?subscription=success|cancelled`.
 *
 * Failures reject with fetchWrapper's `{ status, code, params, message }`.
 */
export const useRoleSubscription = () => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const base = `${config.apiBaseUrl}/me/subscription`;
  const [state, setState] = useState({ status: 'loading', overview: null, error: null });
  const readRef = useRef(0);

  const reload = useCallback(async () => {
    readRef.current += 1;
    const read = readRef.current;
    setState((current) => ({ ...current, status: current.overview ? 'ready' : 'loading', error: null }));
    try {
      const overview = await fetchWrapperRef.current.get(base);
      if (readRef.current === read) setState({ status: 'ready', overview, error: null });
      return overview;
    } catch (error) {
      if (readRef.current === read) setState((current) => ({ ...current, status: current.overview ? 'ready' : 'error', error }));
      throw error;
    }
  }, [base]);

  useEffect(() => {
    reload().catch(() => {});
    return () => { readRef.current += 1; };
  }, [reload]);

  const preview = useCallback((role, interval, { replaceScheduled } = {}) =>
    fetchWrapperRef.current.post(`${base}/preview`, {
      role,
      interval,
      ...(replaceScheduled ? { replace_scheduled: true } : {}),
    }), [base]);

  const change = useCallback(async (role, interval, { prorationDate, replaceScheduled } = {}) => {
    const answer = await fetchWrapperRef.current.post(`${base}/change`, {
      role,
      interval,
      ...(prorationDate ? { proration_date: prorationDate } : {}),
      ...(replaceScheduled ? { replace_scheduled: true } : {}),
    });
    await reload().catch(() => {});
    return answer;
  }, [base, reload]);

  const cancel = useCallback(async () => {
    const answer = await fetchWrapperRef.current.post(`${base}/cancel`, {});
    await reload().catch(() => {});
    return answer;
  }, [base, reload]);

  const checkout = useCallback(async (role, interval) => {
    const answer = await fetchWrapperRef.current.post(`${base}/checkout`, { role, interval, return_path: UPGRADE_PATH });
    if (answer?.checkout_url) window.location.assign(answer.checkout_url);
    return answer;
  }, [base]);

  return { ...state, reload, preview, change, cancel, checkout };
};
